import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_RUBRIC_PASS_POINTS, QUIZ_HINT_COST_POINTS, QUIZ_RUBRIC_MAX_POINTS, QUIZ_GAME_MAX_DELTA } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { rubricPointsToScoreDelta, formatRubricAsGamePointsLabel } from "../../utils/quizScoring";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";
import "./Scenario3.css";

const SC3_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
const SC3_HINT_THEME_CLASS = `${SC3_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();
const SC3_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM =
	"Tras escuchar las declaraciones de la mujer, del hombre y de un testigo presencial, selecciona las 5 actuaciones que aportarían más información útil para esclarecer los hechos en este momento.";

const RETROALIMENTACION =
	"Las cinco opciones con mayor valor informativo inmediato suelen ser: precisar tiempos y detección de la desaparición (A), buscar grabaciones de terceros (B), identificar testigos cercanos y tomarles declaración (C), contrastar la actividad previa del hombre (D) e inspeccionar el entorno por la cartera (E). Pedir solo el contenido de la cartera (F), antecedentes (I) o un registro invasivo (J) aporta menos o exige más fundamento; pedir opiniones (G) o cerrar por coherencia narrativa (H) no sustituye la comprobación objetiva.";

const HINT_HTML = `<div class="txtTexto1 mb-2">Piensa en <b>datos verificables</b> (tiempos, testigos, vídeos, escena física) y en <b>contrastar versiones</b>. Evita actuaciones basadas en opiniones de curiosos o en valorar la acusación como prueba.</div>`;

/** Número exacto de opciones a marcar. */
const REQUIRED_SELECTIONS = 5;

const OPTIONS = [
	{
		id: "A",
		label: "A",
		points: 20,
		text: "Profundizar con la mujer para determinar el momento exacto en el que vio por última vez la cartera y cuándo detectó su desaparición.",
		meta: "Correcta · Valor: Muy alto",
	},
	{
		id: "B",
		label: "B",
		points: 20,
		text: "Solicitar a los ciudadanos que estaban más próximos que indiquen si alguno dispone de grabaciones realizadas antes del inicio de la discusión.",
		meta: "Correcta · Valor: Muy alto",
	},
	{
		id: "C",
		label: "C",
		points: 20,
		text: "Identificar a las personas que se encontraban cerca de ambos implicados antes de que comenzara la discusión y recoger una primera manifestación de los hechos.",
		meta: "Correcta · Valor: Muy alto",
	},
	{
		id: "D",
		label: "D",
		points: 20,
		text: "Preguntar al hombre si puede explicar qué estaba haciendo en la plaza durante los minutos previos a la acusación.",
		meta: "Correcta · Valor: Alto",
	},
	{
		id: "E",
		label: "E",
		points: 20,
		text: "Realizar una inspección visual del entorno inmediato para comprobar si la cartera pudiera encontrarse en las proximidades.",
		meta: "Correcta · Valor: Muy alto",
	},
	{
		id: "F",
		label: "F",
		points: 10,
		text: "Solicitar a la mujer una descripción detallada del contenido de la cartera, incluyendo documentación, tarjetas y efectivo que afirma llevar.",
		meta: "Parcialmente útil",
	},
	{
		id: "G",
		label: "G",
		points: 0,
		text: "Preguntar a los curiosos cuál de las dos personas les parece más creíble según lo que han observado.",
		meta: "Incorrecta · Genera opiniones, no hechos",
	},
	{
		id: "H",
		label: "H",
		points: 0,
		text: "Considerar que la acusación tiene suficiente consistencia porque la mujer mantiene la misma versión desde el inicio.",
		meta: "Incorrecta · Valoración prematura",
	},
	{
		id: "I",
		label: "I",
		points: 5,
		text: "Comprobar si existen antecedentes policiales relacionados con alguno de los implicados antes de continuar con la intervención.",
		meta: "Puede ser útil en otros contextos; no aclara lo que ocurre ahora",
	},
	{
		id: "J",
		label: "J",
		points: 10,
		text: "Registrar inmediatamente al hombre para descartar que lleve la cartera encima.",
		meta: "Parece útil, pero es invasivo y no es necesariamente lo prioritario",
	},
];

const POINTS_BY_ID = Object.fromEntries(OPTIONS.map((o) => [o.id, o.points]));

function sumRubricPoints(selectedIds) {
	let sum = 0;
	for (const id of selectedIds) {
		sum += POINTS_BY_ID[id] ?? 0;
	}
	return sum;
}

export default function Scenario3Quiz3() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.SCENARIO3_ACTUATIONS_PRIORITY_QUIZ;
	const SCENARIO_ID = SCENARIOS.SCENARIO_3;

	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "violet");

	const displayOptions = useMemo(() => OPTIONS, []);

	const toggleOption = (id) => {
		if (isSubmitting || isCompleted) return;
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
				return next;
			}
			if (next.size >= REQUIRED_SELECTIONS) return prev;
			next.add(id);
			return next;
		});
	};

	async function submitAnswer(finalScore, correct, extra = {}) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO3_CONTEXT4,
				correct,
				...extra,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO3_CONTEXT4);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO3_CONTEXT4);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_HTML,
				hintMarkHtml: SC3_HINT_MARK_HTML,
				color: "violet",
				big: false,
				themePopupClass: SC3_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			imageHintUsed,
			hint: HINT_HTML,
			hintMarkHtml: SC3_HINT_MARK_HTML,
			color: "violet",
			big: false,
			themePopupClass: SC3_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(QUIZ_HINT_COST_POINTS);
		}
	};

	function finishQuestion(rubricPointsAwarded) {
		const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
		const scoreFromRubric = rubricPointsToScoreDelta(
			rubricPointsAwarded,
			QUIZ_RUBRIC_MAX_POINTS,
			QUIZ_GAME_MAX_DELTA
		);
		const attemptDelta = scoreFromRubric - hintThisAttempt;
		const newScore = pendingScore + attemptDelta;
		if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

		const capitalInicio = capitalAtQuizStartRef.current ?? 0;
		const netoEnEstaPregunta = newScore - capitalInicio;
		const rubricPass = rubricPointsAwarded >= QUIZ_RUBRIC_PASS_POINTS;
		const avatarUrl = rubricPass ? getRandomAciertoAvatar() : getRandomErrorAvatar();
		const outcomeStyle = rubricPass ? "correct" : "incorrect";

		modalQuestionResolution({
			correct: rubricPass,
			question: "Actuaciones prioritarias",
			textHtml: `${buildQuizResultAvatarHtml(avatarUrl, outcomeStyle, "violet")}
              <div class="txt28 bold pt-3">${formatRubricAsGamePointsLabel(rubricPointsAwarded)}</div>
              <div class="txt22 mt-2">${rubricPass ? "Has alcanzado el umbral de acierto." : "No has alcanzado el umbral de acierto."}</div>
              <div class="txt20 mt-2">Puntos en el marcador (esta pregunta): <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt${
					hintThisAttempt ? ` (incluye −${hintThisAttempt} pt por pista)` : ""
				}</div>
              ${retroalimentacionBlockHtml}`,
			customConfirmButtonHtml: "Continuar",
			color: "violet",
			themePopupClass: SC3_SWAL_POPUP_CLASS,
		}).then(() => {
			submitAnswer(newScore, rubricPass, { rubricPoints: rubricPointsAwarded });
		});
	}

	function checkAnswer() {
		if (selectedIds.size !== REQUIRED_SELECTIONS) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 violet">Debes seleccionar exactamente ${REQUIRED_SELECTIONS} opciones antes de comprobar la respuesta.</div>`,
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "violet",
			themePopupClass: SC3_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const rubricPointsAwarded = sumRubricPoints(selectedIds);
			finishQuestion(rubricPointsAwarded);
		});
	}

	const nSel = selectedIds.size;

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc3-chrono-quiz sc3-quiz1-classify sc3-quiz3-actuations">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero">
						<div className="sc3-chrono-hero-head">
							<h1 className="sg5-title">Pregunta</h1>
							<span className="start-game-badge sg5-badge sc3-chrono-hero-badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 3 · Evolución
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem sc3-chrono-stem sc3-quiz1-classify-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar sc3-chrono-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								Marca exactamente {REQUIRED_SELECTIONS} opciones. La puntuación es la suma de los valores de las opciones elegidas (máximo +{QUIZ_GAME_MAX_DELTA} pt al marcador).
								<span className="sc3-quiz3-selection-count ms-1">
									Seleccionadas: <b>{nSel}</b> / {REQUIRED_SELECTIONS}
								</span>
							</span>
						</p>
					</div>

					<div className="sc2-quiz-options sc3-quiz3-options">
						{displayOptions.map((option) => {
							const isSelected = selectedIds.has(option.id);
							return (
								<button
									key={option.id}
									type="button"
									className={`sg5-aside-card sc2-quiz-option sc3-quiz3-option text-start ${isSelected ? "sc2-quiz-option--selected" : ""}`}
									onClick={() => toggleOption(option.id)}
									aria-pressed={isSelected ? "true" : "false"}
									aria-label={`Opción ${option.label}: ${option.text}`}
								>
									<div className="sc2-quiz-option-row sc3-quiz3-option-row align-items-start">
										<span className="sc2-quiz-option-letter">{option.label}</span>
										<span className="sc2-quiz-option-text sc3-quiz3-option-text">
											<span className="d-block">{option.text}</span>
											<span className="sc3-quiz3-option-meta">{option.meta}</span>
										</span>
									</div>
								</button>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${QUIZ_HINT_COST_POINTS} pt)`
									: `Usar pista (−${QUIZ_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{QUIZ_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}
